import { Controller } from "@hotwired/stimulus"
import { editorStore } from "../editor/store"

// Autosave controller — two flavours of persistence depending on page context:
//
//   1. `/editor` (anonymous) or any page with no `data-project-id` on the
//      root element → write to localStorage at `plot-planner:draft-v1`.
//      Purely local, survives reload, never leaves the browser.
//
//   2. `/projects/:id/edit` (signed-in, project context) → POST to the
//      server at `/projects/:id/autosave` every 2 s after a change. Uses
//      Rails optimistic locking (`lock_version`): the client sends what it
//      last saw, the server bumps it atomically, and a 409 surfaces any
//      concurrent write so the UI can show a conflict banner.
//
// Events emitted for the status indicator (`save_status_controller`):
//   - autosave:pending  — a save is scheduled (debounce window open)
//   - autosave:saving   — POST is in flight
//   - autosave:saved    — 200, with { at: Date }
//   - autosave:offline  — network error, will retry on `online`
//   - autosave:conflict — 409, with { lockVersion, state, updatedAt }
//   - autosave:error    — non-recoverable (e.g. 413, 422) — we stop auto-saving
//                         until the user reloads
const ANON_KEY = "plot-planner:draft-v1"
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000
const DEBOUNCE_MS = 2000

export default class extends Controller {
  connect() {
    this._mode = this.#resolveMode()
    if (this._mode === "server") {
      // Track the lock_version we last saw — advances on every 200.
      this._lockVersion = Number(this.element.dataset.projectLockVersion || 0)
    } else {
      this.#restoreFromLocalStorage()
    }

    const schedule = () => this.#schedule()
    editorStore.addEventListener("plot:change", schedule)
    editorStore.addEventListener("objects:change", schedule)
    editorStore.addEventListener("norms:change", schedule)
    editorStore.addEventListener("ui:change", schedule)
    this._unsubscribe = () => {
      editorStore.removeEventListener("plot:change", schedule)
      editorStore.removeEventListener("objects:change", schedule)
      editorStore.removeEventListener("norms:change", schedule)
      editorStore.removeEventListener("ui:change", schedule)
    }

    this._onOnline = () => { if (this._pendingOffline) this.#flush() }
    window.addEventListener("online", this._onOnline)

    // `disconnect` does not reliably fire during a full page navigation
    // (reload, Back button, typing a new URL) before the DOM tears down —
    // Stimulus's teardown races with the browser unload. Without this, a
    // change made in the last 2 s before navigation is lost: debounce timer
    // still pending, no flush ever runs. `pagehide` is the right hook for
    // "the page is going away" (also covers bfcache). Paired with
    // `keepalive: true` on the fetch so the browser doesn't cancel the
    // in-flight POST when the tab unloads.
    this._onPageHide = () => {
      if (this._timer) { clearTimeout(this._timer); this._timer = null }
      this.#flush({ final: true })
    }
    window.addEventListener("pagehide", this._onPageHide)
  }

  disconnect() {
    if (this._timer) clearTimeout(this._timer)
    this.#flush({ final: true })
    if (this._unsubscribe) this._unsubscribe()
    window.removeEventListener("online", this._onOnline)
    window.removeEventListener("pagehide", this._onPageHide)
  }

  #resolveMode() {
    // Public sandbox pages don't mount this controller at all (see
    // editor/show.html.erb), so we never need to detect that here.
    const id = this.element.dataset.projectId
    const signedIn = this.element.dataset.userSignedIn === "true"
    return id && signedIn ? "server" : "local"
  }

  #schedule() {
    // Stop rescheduling once we've hit an unrecoverable error — user needs to
    // reload after fixing the underlying issue.
    if (this._fatal) return
    // Silent state loads (restoreDraft during server hydration) fire the same
    // change events as user edits; we don't want those to save since they'd
    // just echo the server's current state back with a stale lock_version.
    if (editorStore._hydrating) return
    if (this._timer) clearTimeout(this._timer)
    this._timer = setTimeout(() => this.#flush(), DEBOUNCE_MS)
    this.#emit("autosave:pending")
  }

  #flush({ final = false } = {}) {
    this._timer = null
    if (this._mode === "server") {
      this.#flushServer({ final })
    } else {
      this.#flushLocal()
    }
  }

  #buildState() {
    return {
      plot: editorStore.plot,
      objects: editorStore.objects,
      norms: {
        ruleSetLocale: editorStore.norms.ruleSetLocale,
        ruleSetVersion: editorStore.norms.ruleSetVersion,
        disabledRuleIds: [...editorStore.norms.disabledRuleIds],
      },
      ui: {
        showEdgeDimensions: editorStore.ui.showEdgeDimensions,
        satelliteBg: {
          enabled: editorStore.ui.satelliteBg.enabled,
          opacity: editorStore.ui.satelliteBg.opacity,
          provider: editorStore.ui.satelliteBg.provider,
        },
      },
    }
  }

  #flushLocal() {
    try {
      const payload = { version: 1, savedAt: Date.now(), ...this.#buildState() }
      localStorage.setItem(ANON_KEY, JSON.stringify(payload))
      this.#emit("autosave:saved", { at: new Date() })
    } catch (e) {
      // Private mode or storage full — silent; next save retries.
    }
  }

  async #flushServer({ final = false } = {}) {
    // Reject overlapping flushes — a duplicate `online` event, a fast
    // subsequent user edit, or a `pagehide` firing while a prior save is
    // still in flight would otherwise send two PATCHes with the same
    // `_lockVersion` and the second would get a spurious 409.
    if (this._saveInFlight) return
    this._saveInFlight = true

    const id = this.element.dataset.projectId
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const state = this.#buildState()
    const body = JSON.stringify({
      project: {
        state,
        schema_version: 1,
        lock_version: this._lockVersion,
      },
    })

    this.#emit("autosave:saving")

    try {
      const res = await fetch(`/projects/${id}/autosave`, {
        method: "PATCH",
        credentials: "same-origin",
        keepalive: final, // let pagehide-triggered final save complete
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken || "",
        },
        body,
      })

      if (res.status === 200) {
        const data = await res.json()
        this._lockVersion = data.lock_version
        this._pendingOffline = false
        this.#emit("autosave:saved", { at: new Date(data.updated_at) })
        return
      }

      if (res.status === 409) {
        const data = await res.json()
        // Don't keep retrying — user must pick Update or Overwrite.
        this._fatal = true
        this.#emit("autosave:conflict", {
          lockVersion: data.lock_version,
          state: data.state,
          updatedAt: data.updated_at,
        })
        return
      }

      if (res.status === 413 || res.status === 422) {
        // Unrecoverable without user action. Stop autosaving.
        this._fatal = true
        const data = await res.json().catch(() => ({}))
        this.#emit("autosave:error", { status: res.status, error: data.error })
        return
      }

      // 401/403/5xx — treat as offline and retry on next online event.
      this._pendingOffline = true
      this.#emit("autosave:offline", { status: res.status })
    } catch (e) {
      // Network error — treat as offline.
      this._pendingOffline = true
      this.#emit("autosave:offline")
    } finally {
      this._saveInFlight = false
    }
  }

  #restoreFromLocalStorage() {
    let raw
    try { raw = localStorage.getItem(ANON_KEY) } catch (e) { return }
    if (!raw) return
    try {
      const payload = JSON.parse(raw)
      if (!payload || payload.version !== 1) return
      if (Date.now() - payload.savedAt > MAX_AGE_MS) return
      if (!payload.plot || !Array.isArray(payload.objects)) return
      // Don't clobber a plot seeded from server hydration or cadastre fetch.
      if (editorStore.plot && editorStore.plot.source !== "demo") return
      editorStore.restoreDraft({
        plot: payload.plot,
        objects: payload.objects,
        norms: payload.norms,
        ui: payload.ui,
      })
    } catch (e) {
      // Corrupt payload — ignore; user will rebuild.
    }
  }

  #emit(name, detail = {}) {
    this.element.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }))
  }

  // Public: conflict-banner calls this when the user clicks "Overwrite" —
  // we push our current state over the server's with the fresh lock_version.
  // Clear the debounce timer first so a pending scheduled flush doesn't
  // also fire (would duplicate the network request).
  forceOverwrite(freshLockVersion) {
    if (this._timer) { clearTimeout(this._timer); this._timer = null }
    this._lockVersion = freshLockVersion
    this._fatal = false
    this.#flush()
  }
}
